import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ContractItem } from '@core/models/contract-item.model';
import { ContractNatioNego } from '@core/models/contract-natio-nego.model';
import { ContractNatioNegoCondition } from '@core/models/contract-natio-nego-condition.model';
import { Contract } from '@core/models/contract.model';
import { ContractNatioNegoService } from '@core/services/contract-natio-nego/contract-natio-nego.service';
import { NatioNegoManagingDialogComponent } from '@shared/contract/contract-item/contract-natio-nego/natio-nego-managing-dialog/natio-nego-managing-dialog.component';
import { Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';

type ConditionSummaryRow = Partial<Record<string, string>>;

interface NegotiationCard {
  negotiation: ContractNatioNego;
  tables: { label: string; rows: ConditionSummaryRow[] }[];
}

@Component({
  standalone: false,
  selector: 'app-contract-natio-nego',
  templateUrl: './contract-natio-nego.component.html',
  styleUrls: ['./contract-natio-nego.component.scss'],
})
export class ContractNatioNegoComponent implements OnInit, OnDestroy {
  @Input() contract: Contract;
  @Input() contractItem: ContractItem;
  private negotiationList: ContractNatioNego[] = [];
  negotiationCards: NegotiationCard[] = [];
  readonly conditionSummaryColumns = [
    { id: 7, label: 'Cible' },
    { id: 1, label: 'Annonceur' },
    { id: 'number-1', label: 'N° annonceur' },
    { id: 2, label: 'Mandataire / Agence' },
    { id: 'number-2', label: 'N° mand./ag.' },
    { id: 4, label: 'Produit' },
    { id: 'number-4', label: 'N° produit' },
    { id: 3, label: 'Secodip' },
    { id: 6, label: 'Campagne' },
  ];
  readonly conditionSummaryColumnIds = this.conditionSummaryColumns.map(column => `category-${column.id}`);

  @Input()
  set natioNegotiationList(negotiations: ContractNatioNego[]) {
    this.negotiationList = negotiations ?? [];
    this.negotiationCards = this.negotiationList.map(negotiation => {
      const groups = (negotiation.groupedConditions ?? []).filter(group => group?.length > 0);
      return {
        negotiation,
        tables: [
          {
            label: 'Conditions',
            rows: this.buildConditionSummary(groups.filter(group => group[0].isInclude === 1)),
          },
          ...groups.filter(group => group[0].isInclude === 0).map(group => ({
            label: `Conditions exclues — Groupe ${group[0].groupId}`,
            rows: this.buildConditionSummary([group]),
          })),
        ].filter(table => table.rows.length > 0),
      };
    });
  }

  get natioNegotiationList(): ContractNatioNego[] {
    return this.negotiationList;
  }

  private filter = {};
  public isContractNatioNegoLoading = false;
  public isSavingNatioNego = false;
  itemsFilterForm: FormGroup;
  componentDestroyed$: Subject<void> = new Subject();

  get isNatioNegoBusy(): boolean {
    return this.isSavingNatioNego || this.isContractNatioNegoLoading;
  }

  constructor(
    private fb: FormBuilder,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    private contractNatioNegoService: ContractNatioNegoService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.getContractNatioNegoList();
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.unsubscribe();
  }

  public initForm(): void {
    this.itemsFilterForm = this.fb.group({
      startDate: [null, [Validators.required]],
      endDate: [null, [Validators.required]],
      search: [null, [Validators.required]],
    });
  }

  private buildConditionSummary(groups: ContractNatioNegoCondition[][]): ConditionSummaryRow[] {
    if (groups.length === 0) return [];

    return groups.reduce<ConditionSummaryRow[]>(
      (rows, conditions) => rows.flatMap(row => conditions.map(condition => ({
        ...row,
        [condition.categoryType.id]: String(
          condition.categoryValue?.labelCategoryValue ?? condition.categoryValue?.idCategoryValue ?? ''
        ),
        [`number-${condition.categoryType.id}`]: String(condition.categoryValue?.idCategoryValue ?? ''),
      }))),
      [{}]
    );
  }

  private getContractNatioNegoList(reloadAfterSave = false) {
    this.isContractNatioNegoLoading = true;
    this.filter = Object.assign(this.filter, {
      contractItemId: this.contractItem ? this.contractItem.id : '',
    });

    this.contractNatioNegoService
      .getList(this.filter)
      .pipe(
        take(1),
        takeUntil(this.componentDestroyed$),
        finalize(() => {
          this.isContractNatioNegoLoading = false;
          if (reloadAfterSave) this.isSavingNatioNego = false;
        })
      )
      .subscribe(
        contractNatioNegoList => {
          this.natioNegotiationList = contractNatioNegoList;
        },
        error => {
          this.isContractNatioNegoLoading = false;
        }
      );
  }

  public openNatioNegoModal(natioNegoItem = null, isDuplication = false): void {
    // TODO : delete contract with @bvu view
    const dialogRef = this.dialog.open(NatioNegoManagingDialogComponent, {
      maxHeight: 'calc(100dvh - 32px)',
      maxWidth: 'calc(100vw - 32px)',
      data: {
        contract: this.contract,
        contractItem: this.contractItem,
        negotiationData: natioNegoItem,
        isDuplication: isDuplication,
      },
      panelClass: 'tv-dialog-container',
    });

    dialogRef.componentInstance.saveStarted
      .pipe(takeUntil(dialogRef.afterClosed()), takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.isSavingNatioNego = true;
      });

    dialogRef.componentInstance.saveFinished
      .pipe(takeUntil(dialogRef.afterClosed()), takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.getContractNatioNegoList(true);
      });
  }

  public removeNatioNegotiation(natioNegoItem = null): void {
    this.contractNatioNegoService.remove(natioNegoItem.id.toString()).subscribe(
      result => {
        this.snackBar.open('Suppression de la négociation.', null, {
          duration: 4000,
          verticalPosition: 'top',
          panelClass: ['tv-chip-success'],
        });
        this.getContractNatioNegoList();
      },
      error => {
        this.snackBar.open('Une erreur est survenue.', null, {
          duration: 4000,
          verticalPosition: 'top',
          panelClass: ['tv-chip-error'],
        });
      }
    );
  }

  // TODO : implement search by filter method
  // public valueChange(): void {
  //   merge(this.itemsFilterForm.get('startDate').valueChanges, this.itemsFilterForm.get('endDate').valueChanges)
  //     .pipe(takeUntil(this.componentDestroyed$))
  //     .subscribe(
  //       () => {
  //         // TODO
  //       },
  //       (error: Error) => {
  //         console.error(error);
  //       }
  //     );
  // }
}
